// ---------------------------------------------------------------------------
// V25 — THE TWO SCREENS.
//
//   `portal.account`         S7 of `18-client-portal.md`, deferred to this slice
//                            because every control on it writes a preference V25
//                            owns (`MORNING-2026-08-15.md` §2.33).
//   `console.notifications`  The agency's delivery ledger and the drain control.
//
// ONE FILE FOR BOTH BECAUSE THEY SHARE ONE COMPONENT. The settings block is the
// same rows, the same endpoint and the same refusals whichever surface a person
// is on — a client turning off roster emails and a scheduler turning off the
// same thing are one screen with two shells around it. Two files would be two
// copies of the one thing in this slice most likely to drift.
//
// ---------------------------------------------------------------------------
// NO NEW CSS, AND THAT IS DELIBERATE RATHER THAN LAZY.
//
// `11-…` §9.1's per-surface token sets are complete and `verify-design-parity.sh`
// measures them; every class used here — `stack`, `panel`, `data-table`, `field`,
// `btn`, `muted`, `numeric` — already carries a rule in `web/src/design`. Run 42
// found TWELVE buttons across three earlier slices spelling `className="button"`
// against a stylesheet whose class is `btn`, unstyled since V21 and invisible to
// every test. Adding a class here would be adding the nineteenth.
//
// ---------------------------------------------------------------------------
// A PREFERENCE IS A BUTTON PER ROW, NOT A CHECKBOX.
//
// `11-…`'s rule that status is never colour alone applies to a switch too: an
// unlabelled toggle IS a status conveyed by position, which a person reading a
// cracked phone in the rain resolves by guessing. So each row states what it is
// doing now — with an icon and a word, through `StatusChip` — and offers one
// button that says what pressing it will do.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useState } from "react";

import { send } from "../api/auth";
import {
  notifications,
  type DrainResult,
  type NotificationDelivery,
  type NotificationSettings,
} from "../api/notifications";
import { AddButton } from "../design/AddLink";
import { StatusChip } from "../design/StatusChip";
import { Tabs } from "../design/Tabs";
import { useT } from "../i18n/LocaleProvider";
import { translateLabel } from "../i18n/translate";
import { useSession } from "../session/SessionProvider";
import { whenIn } from "../system/format";

// ---------------------------------------------------------------------------
// THE SHARED SETTINGS BLOCK
// ---------------------------------------------------------------------------

function NotificationSettingsPanel() {
  const { apiBase } = useSession();
  // S145: rendered only on the client portal's Account screen, so it is worded for the client.
  const { t, refusal, locale } = useT();
  const [data, setData] = useState<NotificationSettings | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editingQuiet, setEditingQuiet] = useState(false);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = useCallback(async () => {
    const res = await send<NotificationSettings & { error?: { message: string } }>(
      notifications.settings(apiBase),
    );
    // THE PAYLOAD IS AT THE TOP LEVEL AND THAT IS WHAT IS READ. `ok()` in the
    // Worker returns exactly what it is given — it wraps nothing — and V24
    // shipped four screens that reached inside for a key that does not exist,
    // rendering nothing against the real API while the fixtures agreed with them.
    // `preferences` is the field the handler returns; there is no envelope.
    if (res.status === 200 && res.body && Array.isArray(res.body.preferences)) {
      setData(res.body);
      setFrom(res.body.quiet_window?.from ?? "");
      setTo(res.body.quiet_window?.to ?? "");
      setFailed(null);
    } else {
      setFailed(refusal(res.body?.error) ?? t("These settings could not be read."));
    }
  }, [apiBase, t, refusal]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = useCallback(
    async (payload: Parameters<typeof notifications.updateSettings>[1]) => {
      setBusy(true);
      const res = await send<NotificationSettings & { error?: { message: string } }>(
        notifications.updateSettings(apiBase, payload),
      );
      setBusy(false);
      if (res.status >= 400) {
        setFailed(refusal(res.body?.error) ?? t("That could not be saved."));
        return;
      }
      // THE HANDLER ANSWERS WITH THE SETTINGS AS THEY NOW ARE, so the screen
      // shows what the server holds rather than what it just sent. A screen that
      // renders its own optimistic guess is a screen that disagrees with the
      // database the first time a rule refuses half a change.
      if (res.body && Array.isArray(res.body.preferences)) {
        setData(res.body);
        setFrom(res.body.quiet_window?.from ?? "");
        setTo(res.body.quiet_window?.to ?? "");
      }
      setFailed(null);
      setSaid(t("Saved."));
      setEditingQuiet(false);
    },
    [apiBase, t, refusal],
  );

  // A channel that cannot deliver yet is said once, not as a row per kind.
  const prefs = data?.preferences ?? [];
  const unavailable = [...new Set(prefs.filter((p) => !p.channel_available).map((p) => p.channel_label))];

  const quiet = (
      <section className="panel stack">
        <div className="panel-head">
          <h2>{t("Quiet hours")}</h2>
          {data !== null && !editingQuiet ? (
            <AddButton label={t("Set quiet hours")} expanded={false} onClick={() => setEditingQuiet(true)} />
          ) : null}
        </div>
        <p className="muted">
          {t("Nothing is sent to you inside this window. Anything that would have been is held until it closes, and sent then — it is not thrown away.")}
        </p>
        {failed !== null ? <StatusChip tone="failed" label={failed} /> : null}
        {said !== null ? <StatusChip tone="live" label={said} /> : null}

        {data === null && failed === null ? <p className="muted">{t("Reading your settings…")}</p> : null}

        {data !== null ? (
          <>
            {data.quiet_window === null ? (
              <StatusChip tone="neutral" label={t("No quiet hours. You can be told at any time.")} />
            ) : (
              <StatusChip
                tone="live"
                label={t("Quiet from {from} to {to}, {zone} time", {
                  from: data.quiet_window.from,
                  to: data.quiet_window.to,
                  zone: data.quiet_window.time_zone,
                })}
              />
            )}

            {data.quiet_window !== null && !editingQuiet ? (
              <div className="row">
                <button
                  type="button"
                  className="btn btn-quiet"
                  disabled={busy}
                  onClick={() => void save({ quiet_window: null })}
                >
                  {t("Remove quiet hours")}
                </button>
              </div>
            ) : null}

            {editingQuiet ? (
              <div className="stack">
            <div className="field">
              <label htmlFor="quiet-from">{t("Quiet from")}</label>
              <input
                id="quiet-from"
                type="time"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="quiet-to">{t("Quiet until")}</label>
              <input id="quiet-to" type="time" value={to} onChange={(e) => setTo(e.target.value)} />
              {/* THE TWO CASES A PERSON CANNOT GUESS, SAID ON THE SCREEN. An
                  end before a start is a window across midnight and is the
                  ordinary case here; two identical times is the whole day. */}
              <p className="field-hint">
                {t("An end earlier than the start is a window across midnight — 22:00 until 07:00 is nine quiet hours overnight. The same time twice means all day, every day.")}{" "}
                {data.time_zone
                  ? t("Times are read in {zone} time.", { zone: data.time_zone })
                  : t("Times are read in your agency's time.")}
              </p>
            </div>

            <div className="row">
              <button
                type="button"
                className="btn"
                disabled={busy || from === "" || to === ""}
                onClick={() => void save({ quiet_window: { from, to } })}
              >
                {t("Save quiet hours")}
              </button>
              <button type="button" className="btn btn-quiet" onClick={() => setEditingQuiet(false)}>
                {t("Cancel")}
              </button>
            </div>
              </div>
            ) : null}
          </>
        ) : null}
      </section>
  );

  const kinds = (
      <section className="panel">
        <h2>{t("What you are told about")}</h2>

        {data !== null && data.preferences.length === 0 ? (
          <p className="muted">
            {t("There is nothing to choose yet. This fills up as your agency starts using the things that send you a message.")}
          </p>
        ) : null}

        {unavailable.map((channel) => (
          // A SWITCH THAT WOULD CHANGE NOTHING SAYS SO. The channel exists as a
          // choice before it exists as a delivery path, and pretending otherwise
          // is how a person turns something on and waits for a message that was
          // never coming. Said once for the channel, not once per kind.
          <p className="muted" key={channel}>
            {t("{channel} is not available yet.", { channel: translateLabel(locale, channel) })}
          </p>
        ))}

        {data !== null && prefs.some((p) => p.channel_available) ? (
          <table className="data-table">
            <thead>
              <tr>
                <th>{t("What")}</th>
                <th>{t("How")}</th>
                <th>{t("Now")}</th>
                <th>{t("Change")}</th>
              </tr>
            </thead>
            <tbody>
              {prefs.filter((p) => p.channel_available).map((p) => (
                <tr key={`${p.kind}-${p.channel}`}>
                  <td>{translateLabel(locale, p.kind_label)}</td>
                  <td>{translateLabel(locale, p.channel_label)}</td>
                  <td>
                    {p.enabled ? (
                      <StatusChip tone="live" label={p.is_default ? t("On (default)") : t("On")} />
                    ) : (
                      <StatusChip tone="neutral" label={p.is_default ? t("Off (default)") : t("Off")} />
                    )}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={busy}
                      onClick={() =>
                        void save({
                          preferences: [{ kind: p.kind, channel: p.channel, enabled: !p.enabled }],
                        })
                      }
                    >
                      {p.enabled ? t("Turn off") : t("Turn on")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </section>
  );

  return (
    <Tabs
      label={t("Notification settings")}
      tabs={[
        { id: "notif-quiet", label: t("Quiet hours"), content: quiet },
        { id: "notif-kinds", label: t("What you are told about"), content: kinds },
      ]}
    />
  );
}

// ---------------------------------------------------------------------------
// S7 — THE CLIENT'S ACCOUNT SCREEN.
//
// `18-…` §7 gives S7 three things: the preferences, the agency's branding, and
// the client-user list. Only the first is something a client CHANGES — the
// branding is set by the agency and viewed here, and a client cannot invite
// their own colleagues, which the agency does from the console V5 already ships.
// So the screen says both of those out loud rather than showing an empty box.
// ---------------------------------------------------------------------------
export function PortalAccount() {
  const { me } = useSession();
  const { t } = useT();
  const agency = me?.authenticated ? (me.active_org?.name ?? null) : null;

  return (
    <div className="stack">
      <h1>{t("Your account")}</h1>

      <section className="panel">
        <p className="muted">
          {agency !== null
            ? t("This portal belongs to {agency}, who set how it looks and who from your side can see it.", { agency })
            : t("This portal belongs to your agency, who set how it looks and who from your side can see it.")}
        </p>
        <p className="muted">
          {t("To add or remove somebody from your side, ask your agency — they hold that list.")}
        </p>
      </section>

      <NotificationSettingsPanel />
    </div>
  );
}

// ---------------------------------------------------------------------------
// THE AGENCY'S DELIVERY LEDGER.
//
// NO RECIPIENT COLUMN, AND THERE IS NO COLUMN TO ADD. `app.notification_ledger`
// returns kinds, channels, states, reasons and times; it has no output parameter
// that could carry an account id, a name or an address. A screen cannot show
// what the query cannot return, which is a stronger guarantee than a screen that
// declines to render a field it was given.
// ---------------------------------------------------------------------------
export function ConsoleNotifications() {
  const { apiBase, me } = useSession();
  const { locale, t, refusal } = useT();
  const tl = (text: string) => translateLabel(locale, text);
  const orgZone = me !== null && me.authenticated ? (me.active_org?.timezone ?? null) : null;
  const [rows, setRows] = useState<NotificationDelivery[] | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [result, setResult] = useState<DrainResult | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const res = await send<{ deliveries?: NotificationDelivery[]; error?: { message: string } }>(
      notifications.ledger(apiBase),
    );
    if (res.status === 200 && Array.isArray(res.body?.deliveries)) {
      setRows(res.body.deliveries);
      setFailed(null);
    } else {
      setFailed(refusal(res.body?.error) ?? t("This ledger could not be read."));
    }
  }, [apiBase]);

  useEffect(() => {
    void load();
  }, [load]);

  const drain = useCallback(async () => {
    setBusy(true);
    const res = await send<{ drain?: DrainResult; error?: { message: string } }>(
      notifications.drain(apiBase),
    );
    setBusy(false);
    if (res.status >= 400 || !res.body?.drain) {
      setFailed(refusal(res.body?.error) ?? t("The queue could not be drained."));
      return;
    }
    setResult(res.body.drain);
    setFailed(null);
    await load();
  }, [apiBase, load]);

  return (
    <div className="stack">
      <h1>{t("Notifications")}</h1>

      <p className="muted">{t("Everything this organisation has sent, held or deliberately not sent.")}</p>
      {failed !== null ? <StatusChip tone="failed" label={failed} /> : null}
      {result !== null ? (
        <StatusChip
          tone="live"
          label={t("Considered {considered} · sent {sent} · held {held} · not sent {suppressed} · could not send {failed}", {
            considered: result.considered,
            sent: result.sent,
            held: result.deferred,
            suppressed: result.suppressed,
            failed: result.undeliverable,
          })}
        />
      ) : null}

      <section className="panel stack">
        <div className="panel-head">
          <h2>{t("Outbox")}</h2>
          <button type="button" className="btn btn-quiet" disabled={busy} onClick={() => void drain()}>
            {t("Send what is waiting")}
          </button>
        </div>
        {rows === null && failed === null ? <p className="muted">{t("Reading the ledger…")}</p> : null}
        {rows !== null && rows.length === 0 ? (
          <p className="muted">{t("Nothing has been sent yet.")}</p>
        ) : null}
        {rows !== null && rows.length > 0 ? (
          <table className="data-table">
            <thead>
              <tr>
                <th>{t("What")}</th>
                <th>{t("Event")}</th>
                <th>{t("How")}</th>
                <th>{t("What happened")}</th>
                <th className="numeric">{t("When")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.delivery_id}>
                  <td>{tl(r.kind_label)}</td>
                  <td>{r.event_name ?? "—"}</td>
                  <td>{tl(r.channel_label)}</td>
                  <td>
                    <StatusChip tone={toneFor(r)} label={tl(r.state_label)} />
                    {/* THE SENTENCE, NOT THE CODE. Every suppression reason
                        carries the words a person reads, in the database, beside
                        the id — so a reason added next month arrives here with
                        its explanation rather than as a bare word. */}
                    {r.reason_sentence !== null ? (
                      <>
                        {" "}
                        <span className="muted">{tl(r.reason_sentence)}</span>
                      </>
                    ) : null}
                    {r.deliver_after !== null ? (
                      <>
                        {" "}
                        <span className="muted">{t("Until {when}.", { when: whenIn(r.deliver_after, orgZone) })}</span>
                      </>
                    ) : null}
                    {!r.is_current ? (
                      <>
                        {" "}
                        <span className="muted">{t("Superseded by a later record.")}</span>
                      </>
                    ) : null}
                  </td>
                  <td className="numeric">{whenIn(r.created_at, orgZone)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
        <p className="muted">
          {t("Who each one went to is not on this screen: the query has no column for it, so a delivery record cannot become a way of reading somebody’s inbox.")}
        </p>
      </section>
    </div>
  );
}

/**
 * The tone a delivery state is drawn with, from the state's OWN row.
 *
 * `0072` put it there rather than here, and the argument is worth keeping beside
 * the consumer: the first draft of this function derived the colour from the
 * SHAPE of the row — a `deliver_after` means held, a `reason` means a decision —
 * which places three of the five states and cannot separate a message that was
 * sent from one that could not be. It ended on a comparison that was true for no
 * useful reason, which is a default nobody chose wearing the clothes of logic.
 *
 * Spelling the five ids here instead would have been worse: absolute rule 4, and
 * `HANDOFF.md` §6's standing trap about vocabularies that live in TypeScript
 * where the database census cannot see them.
 *
 * SO THIS FUNCTION VALIDATES RATHER THAN DECIDES. An unrecognised token renders
 * neutral instead of as no chip at all — a status conveyed by absence is the one
 * failure `11-…` §3 will not tolerate — and the four names are the design
 * system's own, which is why naming them here is not naming a taxonomy.
 */
function toneFor(r: NotificationDelivery): "live" | "attention" | "failed" | "neutral" {
  switch (r.state_tone) {
    case "live":
      return "live";
    case "attention":
      return "attention";
    case "failed":
      return "failed";
    default:
      return "neutral";
  }
}
